import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { useState } from 'react';
import { fetchRecipients } from '../../api/ShareWithThirdPartyComponentApi';
import { RecipientType } from '../../api/model/common/RecipientType';
import { displayErrorAlert } from '../../utils/Alerts';
import { isThirdPartyOutage } from '../../utils/IntegrationResponseStatusDeterminer';
import ThirdPartyOutageBanner from '../thirdPartyOutage/ThirdPartyOutageBanner';
import integrations from '../utils/integrations';
import MapGoogleChatAccount from './MapGoogleChatAccount';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getChannelSelectorFallback = appId => integrations.isGoogleChatAppId(appId) ? 'shareWithThirdPartyComponentLibrary.thirdPartyOutageError.spacesSelectorFallback' : 'shareWithThirdPartyComponentLibrary.thirdPartyOutageError.channelsSelectorFallback';
const RecipientSelector = ({
  onChange,
  defaultValue,
  appId,
  recipientType,
  selectorAppearanceConfig,
  disabled
}) => {
  const [options, setOptions] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isAlertDisplayed, setIsAlertDisplayed] = useState(false);
  const [lastQueriedName, setLastQueriedName] = useState('');
  const [thirdPartyOutageOnChannelsLoading, setThirdPartyOutageOnChannelsLoading] = useState(false);
  const [thirdPartyOutageOnUsersLoading, setThirdPartyOutageOnUsersLoading] = useState(false);
  const alertMessageTranslationKey = 'shareWithThirdPartyComponentLibrary.shared.tryAgainMessage';
  const handleSelectedOptionUpdate = event => {
    const id = event.target.value;
    const selectedOption = options.find(option => option.value === id);
    onChange({
      appId,
      id,
      name: selectedOption ? selectedOption.text : undefined,
      type: recipientType
    });
  };
  const mapRecipientToSelectorOption = recipient => {
    return {
      text: recipient.name,
      value: recipient.id
    };
  };
  const processSearchResult = (isInputChanged, searchInput, callback) => {
    return response => {
      if (recipientType === RecipientType.USER) {
        setThirdPartyOutageOnUsersLoading(false);
      }
      if (recipientType === RecipientType.CHANNEL) {
        setThirdPartyOutageOnChannelsLoading(false);
      }
      let newOptions = response.results.map(mapRecipientToSelectorOption);
      if (!isInputChanged) {
        newOptions = [...options, ...newOptions];
      }
      const pagingNextAfter = response.paging && response.paging.next.after;
      const hasMoreRecipientsToFetch = pagingNextAfter != null;
      setNextCursor(pagingNextAfter);
      setLastQueriedName(searchInput);
      setOptions(newOptions);
      setHasMore(hasMoreRecipientsToFetch);
      callback(null, {
        options: newOptions,
        pagination: {
          hasMore: hasMoreRecipientsToFetch
        }
      });
    };
  };
  const filterOptionByTextIncludes = searchInput => {
    return option => {
      return option.text.toLowerCase().indexOf(searchInput) !== -1;
    };
  };
  const searchAmongAlreadyFetchedRecipients = (searchInput, recipientWithinAppOptions) => {
    return {
      options: recipientWithinAppOptions.filter(filterOptionByTextIncludes(searchInput)),
      pagination: {
        hasMore: false
      }
    };
  };
  const setThirdPartyOutageStatus = () => {
    if (recipientType === RecipientType.USER) {
      setThirdPartyOutageOnUsersLoading(true);
    }
    if (recipientType === RecipientType.CHANNEL) {
      setThirdPartyOutageOnChannelsLoading(true);
    }
  };
  const loadOptions = (searchInput, callback) => {
    if (searchInput.indexOf(lastQueriedName) !== -1 && !hasMore) {
      callback(null, searchAmongAlreadyFetchedRecipients(searchInput, options));
      return;
    }
    const isInputChanged = lastQueriedName !== searchInput;
    const nextCursorToCallWith = isInputChanged ? null : nextCursor;
    fetchRecipients(appId, searchInput, recipientType, nextCursorToCallWith).then(processSearchResult(isInputChanged, searchInput, callback)).catch(error => {
      if (isThirdPartyOutage(error.responseJSON)) {
        setThirdPartyOutageStatus();
        return;
      }
      if (!isAlertDisplayed) {
        setIsAlertDisplayed(true);
        displayErrorAlert(selectorAppearanceConfig.alertTitleTranslationKey, alertMessageTranslationKey);
      }
    });
  };
  const extractDefaultOptionValue = () => {
    if (defaultValue === undefined) {
      throw new Error(`Failed to extract default option value for select. defaultValue prop undefined!`);
    }
    if (defaultValue.name === undefined) {
      return defaultValue.id;
    }
    return defaultValue.name;
  };
  const thirdPartyOutage = thirdPartyOutageOnChannelsLoading || thirdPartyOutageOnUsersLoading;
  const getSelectorValidationMessageKey = () => {
    if (!thirdPartyOutage) {
      return '';
    }
    if (recipientType === RecipientType.USER) {
      return 'shareWithThirdPartyComponentLibrary.thirdPartyOutageError.usersSelectorFallback';
    }
    if (recipientType === RecipientType.CHANNEL) {
      return getChannelSelectorFallback(appId);
    }
    return '';
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ThirdPartyOutageBanner, {
      appId: appId,
      isShown: thirdPartyOutage,
      recipientType: recipientType
    }), /*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(FormControl, {
      label: I18n.text(selectorAppearanceConfig.labelKey),
      help: /*#__PURE__*/_jsx(MapGoogleChatAccount, {
        appId: appId
      }),
      error: thirdPartyOutage,
      validationMessage: thirdPartyOutage && /*#__PURE__*/_jsx(FormattedMessage, {
        message: getSelectorValidationMessageKey()
      }),
      children: /*#__PURE__*/_jsx(Select, {
        "data-test-id": "app-recipient-selector",
        cache: false,
        loadOptions: loadOptions,
        placeholder: I18n.text(selectorAppearanceConfig.placeholderKey),
        onChange: handleSelectedOptionUpdate,
        clearable: selectorAppearanceConfig.clearable,
        defaultValue: defaultValue ? extractDefaultOptionValue() : undefined,
        disabled: disabled
      })
    })]
  });
};
export default RecipientSelector;